import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import i18n from 'i18n-react';

import badges from 'assets/images/badges';

/**
 * Helper function to get translation with fallback
 */
const t = (key, fallback, params = {}) => {
  const translated = i18n.translate(key, params);
  return translated && translated !== key ? translated : fallback;
};

// Tier descriptions based on the ticket requirements
const TIER_DESCRIPTIONS = {
  bronze: {
    en: 'Reward advisors who complete the basics and show initial commitment.',
  },
  silver: {
    en: 'Recognize advisors who consistently sell The Islands of The Bahamas and remain engaged.',
  },
  gold: {
    en: 'Reward top performers who are deeply invested in selling The Bahamas.',
  },
};

const TierCard = ({
  tiers = [],
  currentTier = 'none',
  tierCopy = '',
  tierExpiresAt = null,
  daysUntilExpiry = null,
  progress = null,
}) => {
  const { tierBadges, badgeBronze, badgeSilver, badgeGolden } = badges;

  const safeProgress = progress || {
    isGraduated: false,
    completedModules: 0,
    totalModules: 0,
    periodSalesUsd: 0,
    periodBookingCount: 0,
    nextTier: null,
  };

  const tierBadgeMap = useMemo(
    () => ({
      bronze: badgeBronze,
      silver: badgeSilver,
      gold: badgeGolden,
    }),
    [badgeBronze, badgeSilver, badgeGolden],
  );

  const currentTierIndex = useMemo(() => {
    if (currentTier === 'none') return -1;
    return tiers.findIndex((tier) => tier.key === currentTier);
  }, [currentTier, tiers]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getTierDescription = (tierKey) => {
    const desc = TIER_DESCRIPTIONS[tierKey];
    if (!desc) return '';
    return t(`tierCard.${tierKey}Description`, desc.en);
  };

  const getTierProgress = (tier) => {
    if (!tier) return 0;
    if (tier.requirement_type === 'modules') {
      const { completedItems = 0, count = 1 } = tier;
      return Math.min((completedItems / count) * 100, 100);
    }
    if (tier.requirement_type === 'bookings') {
      const { current_sales = 0, current_bookings = 0, threshold_sales = 1, threshold_bookings = 1 } = tier;
      const salesProgress = (current_sales / threshold_sales) * 100;
      const bookingsProgress = (current_bookings / threshold_bookings) * 100;
      return Math.min(Math.max(salesProgress, bookingsProgress), 100);
    }
    return 0;
  };

  const nextTier = useMemo(() => {
    if (safeProgress.nextTier) {
      return tiers.find((tier) => tier.key === safeProgress.nextTier);
    }
    return tiers.find((tier) => !tier.achieved);
  }, [tiers, safeProgress.nextTier]);

  const displayTier = currentTier === 'none' && tiers.length > 0 ? tiers[0] : null;
  const isWorkingTowardFirstTier = currentTier === 'none';

  const renderTierCard = (tier, index) => {
    const progressValue = getTierProgress(tier);
    const isAchieved = tier.achieved;
    const isCurrent = tier.key === currentTier;

    return (
      <div
        className={clsx('tier-card__tier', `tier-card__tier--${tier.key}`, {
          'tier-card__tier--achieved': isAchieved,
          'tier-card__tier--current': isCurrent,
          'tier-card__tier--locked': !isAchieved && index > currentTierIndex + 1,
        })}
        key={tier.key}
      >
        {isAchieved && (
          <div className="tier-card__achieved-badge">
            <span className="tier-card__checkmark">{'✓'}</span>
            {t('tierCard.achieved', 'Achieved')}
          </div>
        )}

        <div className="tier-card__tier-header">
          <img
            src={tierBadgeMap[tier.key] || tierBadges[index]}
            alt={tier.title}
            className={clsx('tier-card__badge', {
              'tier-card__badge--locked': !isAchieved,
            })}
          />
          <div className="tier-card__tier-info">
            <h4 className="tier-card__tier-title">{t(`tierCard.title.${tier.key}`, tier.title)}</h4>
            <p className="tier-card__tier-requirement">{t(`tierCard.requirement.${tier.key}`, tier.requirement)}</p>
            <p className="tier-card__tier-description">{getTierDescription(tier.key)}</p>
          </div>
        </div>

        <div className="tier-card__progress-container">
          <div className="tier-card__progress-bar">
            <div
              className={clsx('tier-card__progress-fill', {
                'tier-card__progress-fill--complete': progressValue >= 100,
                [`tier-card__progress-fill--${tier.key}`]: true,
              })}
              style={{ width: `${progressValue}%` }}
            />
          </div>
          <div className="tier-card__progress-text">
            {tier.requirement_type === 'modules' && (
              <span className="tier-card__progress-line">
                {tier.completedItems || 0}
                {' / '}
                {tier.count} {t('tierCard.modules', 'modules')}
              </span>
            )}
            {tier.requirement_type === 'bookings' && (
              <>
                <span className="tier-card__progress-line">
                  {t('tierCard.sales', 'Sales')}
                  {': '}
                  {formatCurrency(tier.current_sales || 0)}
                  {' / '}
                  {formatCurrency(tier.threshold_sales || 0)}
                </span>
                <span className="tier-card__progress-line">{t('tierCard.or', 'or')}</span>
                <span className="tier-card__progress-line">
                  {t('tierCard.bookings', 'Bookings')}
                  {': '}
                  {tier.current_bookings || 0}
                  {' / '}
                  {tier.threshold_bookings || 0}
                </span>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Determine the tier key for current section styling
  const currentSectionTierKey = isWorkingTowardFirstTier ? displayTier?.key || 'bronze' : currentTier;

  return (
    <div className="tier-card">
      {/* Current Tier Section */}
      <div className={clsx('tier-card__current-section', `tier-card__current-section--${currentSectionTierKey}`)}>
        <h3 className="tier-card__section-header">
          {isWorkingTowardFirstTier
            ? t('tierCard.workingToward', 'Working Toward')
            : t('tierCard.currentTier', 'Current Tier')}
        </h3>
        <div className="tier-card__status-content">
          {currentTier !== 'none' && tierBadgeMap[currentTier] ? (
            <img src={tierBadgeMap[currentTier]} alt={currentTier} className="tier-card__current-badge" />
          ) : displayTier ? (
            <img
              src={tierBadgeMap[displayTier.key] || tierBadges[0]}
              alt={displayTier.title}
              className="tier-card__current-badge tier-card__current-badge--target"
            />
          ) : (
            <div className="tier-card__no-badge" />
          )}
          <div className="tier-card__status-text">
            <p className="tier-card__copy">
              {isWorkingTowardFirstTier && displayTier
                ? t(
                    'tierCard.workingTowardBronze',
                    `Complete the certification to achieve ${t(`tierCard.title.${displayTier.key}`, displayTier.title)} status!`,
                  )
                : t(
                    'tierCard.tierCopy',
                    tierCopy ||
                      `Congratulations! You have achieved ${t(`tierCard.title.${currentTier}`, currentTier)} status!`,
                    { tier: t(`tierCard.title.${currentTier}`, currentTier) },
                  )}
            </p>
            {isWorkingTowardFirstTier && displayTier && (
              <p className="tier-card__target-requirement">
                {t(`tierCard.requirement.${displayTier.key}`, displayTier.requirement)}
              </p>
            )}
            {/* Bronze doesn't expire - only show expiry for silver and gold */}
            {tierExpiresAt && daysUntilExpiry !== null && currentTier !== 'bronze' && (
              <>
                <p className="tier-card__expiry">
                  {daysUntilExpiry > 0 ? (
                    <>
                      {t('tierCard.expiresIn', 'Expires in')} <strong>{daysUntilExpiry}</strong>{' '}
                      {t('tierCard.days', 'days')}
                    </>
                  ) : (
                    <span className="tier-card__expiry--urgent">{t('tierCard.expiringToday', 'Expiring today!')}</span>
                  )}
                </p>
                <p className="tier-card__expiry-date">
                  {t('tierCard.expiryDate', 'Expiry Date')}
                  {': '}
                  {formatDate(tierExpiresAt)}
                </p>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Next Tier Section */}
      {nextTier && !isWorkingTowardFirstTier && (
        <div className="tier-card__next-section">
          <h3 className="tier-card__section-header">{t('tierCard.nextTier', 'Next Tier')}</h3>
          <div className="tier-card__next-goal">
            <h3 className="tier-card__next-title">
              {t('tierCard.nextGoal', 'Next Goal')}
              {': '}
              <span className={`tier-card__next-name tier-card__next-name--${nextTier.key}`}>
                {t(`tierCard.title.${nextTier.key}`, nextTier.title)}
              </span>
            </h3>
            <p className="tier-card__next-requirement">
              {t(`tierCard.requirement.${nextTier.key}`, nextTier.requirement)}
            </p>
            <p className="tier-card__next-description">{getTierDescription(nextTier.key)}</p>
          </div>
        </div>
      )}

      {/* All Tiers Progress - Horizontal Grid */}
      <div className="tier-card__tiers">
        <h3 className="tier-card__section-title">{t('tierCard.allTiers', 'All Tiers')}</h3>
        <div className="tier-card__tiers-grid">{tiers.map((tier, index) => renderTierCard(tier, index))}</div>
      </div>
    </div>
  );
};

TierCard.propTypes = {
  tiers: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      key: PropTypes.string,
      achieved: PropTypes.bool,
      requirement: PropTypes.string,
      requirement_type: PropTypes.oneOf(['modules', 'bookings']),
    }),
  ),
  currentTier: PropTypes.string,
  tierCopy: PropTypes.string,
  tierExpiresAt: PropTypes.string,
  daysUntilExpiry: PropTypes.number,
  progress: PropTypes.shape({
    nextTier: PropTypes.string,
  }),
};

TierCard.defaultProps = {
  tiers: [],
  currentTier: 'none',
  tierCopy: '',
  tierExpiresAt: null,
  daysUntilExpiry: null,
  progress: null,
};

export default TierCard;
